import * as React from 'react';
import {Popup} from 'react-map-gl';
import {PopupContents} from './popupContents';

import {Contact} from '../../../types';
import {useToggle} from '../../hooks/useToggle';
import {ILayerState, IMapState} from '../../store/types';
import {IContactNode, IFeature_CONO} from '../../types';
import {IPopupInfo} from '../../utils/getPopupInfo';

interface IProps {
    popupInfo: any;
    isCreatingNewEntity: boolean;
    isEditing: boolean;
    layers: string[];
    layerState: ILayerState;
    databaseLayerIds: IMapState['databaseLayerIds'];
    // callbacks
    handleContextMenu: (e: React.MouseEvent<HTMLDivElement>) => void;
    closePopup: (e?: Event) => void;
    toggleEditing: (e: Event) => void;
    createContact: (feature: IFeature_CONO, contactInfo: IContactNode) => void;
    removeEntityContactLink: (feature: IFeature_CONO, contactInfo: IContactNode) => void;
    createEntityContactLink: (feature: IFeature_CONO, contactInfo: IContactNode|Contact) => void;
    editContact: (feature: IFeature_CONO, contactInfo: IContactNode) => void;
    deleteFeature: (id: string, type: string) => void;
    updateFeature: (popupInfo: IPopupInfo & {oldType: string}) => void;
    resetTempFeature: (e?: Event) => void;
    beginEditGeometry: (e: Event) => void;
    bufferExistingFeature: (fullUid: string, editMode: string) => void;
}

export const MapPopup = React.memo((props: IProps) => {
    const {
        // state vars
        popupInfo, isCreatingNewEntity,
        // props vars
        layers, layerState, isEditing, databaseLayerIds,
        // local map callbacks
        handleContextMenu, closePopup, createContact,
        createEntityContactLink, removeEntityContactLink, editContact,
        deleteFeature, updateFeature, resetTempFeature,
        beginEditGeometry, bufferExistingFeature,
    } = props;
    const [isEditingPopup, toggleEditingPopup] = useToggle(false);
    const [isShowingContactDetails, toggleContactDetails] = useToggle(false);

    // tslint:disable-next-line:variable-name
    const _cancel = React.useCallback(() => {
        if (!isCreatingNewEntity) {
            toggleEditingPopup();
        } else {
            closePopup();
        }
    }, [isCreatingNewEntity, toggleEditingPopup, closePopup]);

    React.useEffect(() => {
        toggleEditingPopup(false);
    }, [popupInfo, toggleEditingPopup]);

    if (!popupInfo) { return null; }
    return <div style={{display: isEditing ? 'none' : 'initial'}} onContextMenu={handleContextMenu}>
        <Popup key={'popup'}
               captureScroll={true}
               captureDrag={true}
               captureClick={true}
               tipSize={5}
               closeOnClick={false}
               closeButton={true}
               anchor='top'
               longitude={popupInfo.longitude}
               latitude={popupInfo.latitude}
               onClose={closePopup}>
            <PopupContents
                popupInfo={popupInfo}
                layers={layers}
                layerState={layerState}
                databaseLayerIds={databaseLayerIds}
                isCreatingNewEntity={isCreatingNewEntity}
                isEditing={isEditingPopup}
                toggleEditing={toggleEditingPopup}
                showContactDetails={isShowingContactDetails}
                toggleContactDetails={toggleContactDetails}
                createContact={createContact}
                editContact={editContact}
                editGeometry={beginEditGeometry}
                resetFeature={resetTempFeature}
                createEntityContactLink={createEntityContactLink}
                removeEntityContactLink={removeEntityContactLink}
                buffer={bufferExistingFeature}
                save={updateFeature}
                delete={deleteFeature}
                cancel={_cancel}
            />
        </Popup>
    </div>;
});
